/**
 * Pinning a career to the Vision Board.
 *
 * A learner who finds the role they want shouldn't have to retype it as a
 * dream. This writes the role straight into the Vision Board's dream list —
 * the same shape `/vision-board` reads — through both stores it uses:
 *
 *   - `cloudStorage` (the in-memory, account-backed cache) so the board shows
 *     it immediately, even offline,
 *   - `vision_boards.dreams` so it is there on the next device.
 *
 * Dreams pinned this way carry a `career:<ci_id>` tag, which is how we keep a
 * second tap from creating a duplicate.
 */
import { supabase } from "@/integrations/supabase/client";
import { cloudStorage } from "@/lib/cloudStore";

const DREAMS_KEY = "wekit:vb:dreams:v1";

export type VisionDream = {
  id: string;
  title: string;
  note?: string;
  targetYear?: number;
  priority?: "low" | "medium" | "high";
  tags?: string[];
  createdAt: number;
};

export const careerDreamTag = (ciId: string) => `career:${ciId}`;

function readLocalDreams(): VisionDream[] {
  try {
    const raw = cloudStorage.getItem(DREAMS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? (arr as VisionDream[]) : [];
  } catch {
    return [];
  }
}

function writeLocalDreams(rows: VisionDream[]) {
  try {
    cloudStorage.setItem(DREAMS_KEY, JSON.stringify(rows));
  } catch {
    /* unserialisable — the table copy is the source of truth */
  }
}

/** True when this role is already pinned on the board (local view). */
export function isCareerPinned(ciId: string): boolean {
  const tag = careerDreamTag(ciId);
  return readLocalDreams().some((d) => d.tags?.includes(tag));
}

/** Every ci_id currently pinned as a dream. */
export function pinnedCareerIds(): string[] {
  return readLocalDreams()
    .flatMap((d) => d.tags ?? [])
    .filter((t) => t.startsWith("career:"))
    .map((t) => t.slice("career:".length));
}

/**
 * How many roles a learner may keep on the board at once.
 *
 * Three is deliberate: a board with twenty roles on it isn't a plan, it's a
 * wish list. Three keeps the next step obvious.
 */
export const MAX_CAREER_DREAMS = 3;

/** Roles pinned right now (local view). */
export function pinnedCareerCount(): number {
  return pinnedCareerIds().length;
}

/** Raised when a fourth role is pinned. Carries the roles already there. */
export class CareerDreamLimitError extends Error {
  readonly limit = MAX_CAREER_DREAMS;
  constructor(readonly pinned: string[]) {
    super(`career_dream_limit:${MAX_CAREER_DREAMS}`);
    this.name = "CareerDreamLimitError";
  }
}

function careerIdsIn(rows: VisionDream[]): string[] {
  return rows
    .flatMap((d) => d.tags ?? [])
    .filter((t) => t.startsWith("career:"))
    .map((t) => t.slice("career:".length));
}


function buildDream(career: { ciId: string; title: string; family?: string | null }): VisionDream {
  return {
    id: `career-${career.ciId}`,
    title: career.title,
    note: career.family
      ? `A role you're aiming for in ${career.family}.`
      : "A role you're aiming for.",
    targetYear: new Date().getFullYear() + 5,
    priority: "high",
    tags: [careerDreamTag(career.ciId), ...(career.family ? [career.family] : [])],
    createdAt: Date.now(),
  };
}

/**
 * Pin a role to the Vision Board. Idempotent: pinning the same role twice
 * leaves one dream. Resolves `false` when it was already there.
 *
 * At most {@link MAX_CAREER_DREAMS} roles can sit on the board. A fourth pin
 * raises {@link CareerDreamLimitError} — locally first, so the cap holds even
 * offline, and again against the stored board so two devices can't slip past it.
 */
export async function pinCareerToVisionBoard(
  userId: string | null,
  career: { ciId: string; title: string; family?: string | null },
): Promise<boolean> {
  const tag = careerDreamTag(career.ciId);
  const local = readLocalDreams();
  const already = local.some((d) => d.tags?.includes(tag));
  const localIds = careerIdsIn(local);
  if (!already && localIds.length >= MAX_CAREER_DREAMS) {
    throw new CareerDreamLimitError(localIds);
  }
  const dream = buildDream(career);
  if (!already) writeLocalDreams([dream, ...local]);

  if (!userId) return !already;

  // Merge against the stored board so a dream added on another device survives.
  const { data } = await supabase
    .from("vision_boards")
    .select("dreams")
    .eq("user_id", userId)
    .maybeSingle();

  const remote = Array.isArray(data?.dreams) ? (data!.dreams as VisionDream[]) : [];
  if (remote.some((d) => d.tags?.includes(tag))) {
    // Keep the device in step with the board even if the pin predates it.
    if (!already) writeLocalDreams([...remote]);
    return false;
  }

  const remoteIds = careerIdsIn(remote);
  if (remoteIds.length >= MAX_CAREER_DREAMS) {
    // The board filled up elsewhere. Adopt the stored truth and say so.
    writeLocalDreams([...remote]);
    throw new CareerDreamLimitError(remoteIds);
  }

  const merged = [dream, ...remote];
  const { error } = await supabase.from("vision_boards").upsert(
    { user_id: userId, dreams: merged as never, updated_at: new Date().toISOString() },
    { onConflict: "user_id" },
  );
  if (error) throw error;
  writeLocalDreams(merged);
  return true;

}

/** Unpin a role. Safe to call when it was never pinned. */
export async function unpinCareerFromVisionBoard(
  userId: string | null,
  ciId: string,
): Promise<void> {
  const tag = careerDreamTag(ciId);
  const kept = readLocalDreams().filter((d) => !d.tags?.includes(tag));
  writeLocalDreams(kept);
  if (!userId) return;

  const { data } = await supabase
    .from("vision_boards")
    .select("dreams")
    .eq("user_id", userId)
    .maybeSingle();
  if (!data) return;
  const remote = Array.isArray(data.dreams) ? (data.dreams as VisionDream[]) : [];
  const next = remote.filter((d) => !d.tags?.includes(tag));
  if (next.length === remote.length) return;
  const { error } = await supabase.from("vision_boards").upsert(
    { user_id: userId, dreams: next as never, updated_at: new Date().toISOString() },
    { onConflict: "user_id" },
  );
  if (error) throw error;
}
